import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import inputValidator from "../../atoms/input-validator";
import Input from "../../atoms/input";
import ReCAPTCHA from "react-google-recaptcha";
import Checkbox from "./checkbox";

const Content = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  @media all and (-ms-high-contrast: none) {
    margin: 10px 0px;
  }
`;

const TabWrapper = styled.div`
  display: flex;
  flex: 1 1 0;
  margin: 0;
`;

const Tab = styled.div`
  display: flex;
  width: 50%;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  overflow-wrap: break-word;
  padding: 10px 5px;
  text-align: center;

  font-size: 18px;
  font-weight: bold;
  ${(props) =>
    props.active
      ? `
    border-width: 3px 1px 1px;
    border-style: solid;
    color: ${props.secondaryColor || '#008a16'};
    border-color: ${props.secondaryColor || '#008a16'} rgb(230, 230, 230) rgb(230, 230, 230);
    background-color:#F4F4F4;
  `
      : `
    border: 1px solid rgb(230, 230, 230);
    background-color: white;
  `}
`;

const TabContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
`;

const ContentTitle = styled.span`
  font-weight: bold;
  font-size: 16px;
  white-space: pre-wrap;
  margin-bottom: 10px;
`;

const Required = styled.span`
  color: red;
`;

const Box = styled.div`
  display: flex;
`;

const Policy = styled(Box)`
  display: ${props => (props.visible ? "flex" : "none")};
  flex-direction: column;
  font-size: 14px;
  line-height: 14px;
`;

const Message = styled.textarea`
  padding: 7px 10px;
  font-size: 18px;
  border: 1px solid #e7e6e6;
  resize: none;
`;

const Label = styled.label`
  margin-top: 10px;
  color: #333333;
  font-size: 16px;
  line-height: 20px;
`;

const Captcha = styled(Box)`
  margin: 10px 0px;
`;

const ButtonsGroup = styled(Box)`
  justify-content: space-between;
  padding: 10px 0px;
`;

const SubscribeWrapper = styled(Box)`
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  margin-top: 10px;
`;
const SubscribeCheckbox = styled(Box)`
  margin-right: 10px;
`;
const SubscribeText = styled(Policy)`
  display: inline-block;

  span {
    cursor: pointer;
  }

  a {
    color: ${(props) => props.primaryColor};
    :hover {
      text-decoration: underline;
      cursor: pointer;
    }
  }
`;

const InputBox = styled.div`
  padding: 10px 0px;
`;

const RecaptchaContainer = ({ recaptchaKey, onChange }) => (
  <ReCAPTCHA sitekey={recaptchaKey} onChange={onChange} onExpired={onChange} />
);

const standardize_color = (str) => {
  var ctx = document.createElement('canvas').getContext('2d');
  ctx.fillStyle = str;
  return ctx.fillStyle.length > 0 && ctx.fillStyle[0] === '#' ? ctx.fillStyle : rgb2hex(ctx.fillStyle);
}

const rgb2hex = (rgb) => {
  rgb = rgb.match(/^rgba?[\s+]?\([\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?/i);
  return (rgb && rgb.length === 4) ? "#" +
    ("0" + parseInt(rgb[1], 10).toString(16)).slice(-2) +
    ("0" + parseInt(rgb[2], 10).toString(16)).slice(-2) +
    ("0" + parseInt(rgb[3], 10).toString(16)).slice(-2) : '';
}

const hexToRgb = hex =>
  hex.replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i
    , (m, r, g, b) => '#' + r + r + g + g + b + b)
    .substring(1).match(/.{2}/g)
    .map(x => parseInt(x, 16));


const rgb_is_light = (color) => {
  const brightness = ((color[0] * 299) + (color[1] * 587) + (color[2] * 114)) / 1000;
  return brightness > 155;
}

const display_send_to_myself = "myself";
const display_send_to_others = "others";

class SharedMailComponent extends React.Component {
  static propTypes = {
    onSubmit: PropTypes.func,
    onCancel: PropTypes.func,
    senderName: PropTypes.string,
    senderEmail: PropTypes.string,
  };

  constructor(props) {
    super(props);
    this.state = {
      primaryTextColor: rgb_is_light(hexToRgb(standardize_color(props.primaryColor || '#008a16'))) ? 'rgb(98,100,105)' : 'white',
      displayKey: display_send_to_myself,
      name: props.senderName || "",
      email: props.senderEmail || "",
      recipientName: "",
      recipientEmail: "",
      message: "",
      recaptcha: null,
      subscribe: false
    };
  }

  handleInputValue = (event) => {
    const { name, value } = event.target;
    if (this.state[name] !== value) {
      this.setState({ [name]: value });
    } else {
      return false;
    }
  };

  handleTextareaValue = (event) => {
    return this.handleInputValue(event);
  };

  handleCheckboxInputValue = (newValue) => {
    this.setState({subscribe: newValue});
  }

  submitForm = () => {
    this.props.onSubmit({
      sendToMyself: this.state.displayKey === display_send_to_myself,
      senderName: this.state.name,
      senderEmail: this.state.email,
      recipientName: this.state.recipientName,
      recipientEmail: this.state.recipientEmail,
      message: this.state.message,
      subscribe: this.state.subscribe,
      captchaResponse: this.state.recaptcha,
    });
  };

  withRequired = (label) => (
    <Box id="sharedMailComponent-box">
      {label}
      <Required>*</Required>
    </Box>
  );

  isFormValid = () => {
    let fieldsToValidate = [
      [this.state.name, inputValidator().validateFieldName],
      [this.state.email, inputValidator().validateFieldEmail],
    ];

    if (this.state.displayKey === display_send_to_others) {
      fieldsToValidate = [
        [this.state.name, inputValidator().validateFieldName],
        [this.state.email, inputValidator().validateFieldEmail],
        [this.state.recipientName, inputValidator().validateFieldName],
        [this.state.recipientEmail, inputValidator().validateFieldEmail],
      ];
    }

    return inputValidator().validateFields(fieldsToValidate);
  };

  renderTabOptions = () => {
    return (
      <TabWrapper id="sharedMailComponent-tabWrapper">
        <Tab
          id="sharedMailComponent-tab"
          active={this.state.displayKey === display_send_to_myself}

          onClick={(e) => {
            e.preventDefault();
            this.setState({
              displayKey: display_send_to_myself,
            });
          }}
          secondaryColor={this.props.secondaryColor}
        >
          {this.props.sendTabLabel}
        </Tab>
        <Tab
          active={this.state.displayKey === display_send_to_others}
          onClick={(e) => {
            e.preventDefault();
            this.setState({
              displayKey: display_send_to_others,
            });
          }}
          secondaryColor={this.props.secondaryColor}
        >
          {this.props.shareTabLabel}
        </Tab>
      </TabWrapper>
    );
  };

  renderTabContent = () => {
    const title =
      this.state.displayKey === display_send_to_myself
        ? this.props.sendTitle
        : this.props.shareTitle;

    const disabledProps = {};

    if (!this.isFormValid() || !this.state.recaptcha) {
      disabledProps.disabled = true;
    }

    return (
      <TabContent id="sharedMailComponent-tabContent">
        <ContentTitle>{title}</ContentTitle>
        <InputBox id="sharedMailComponent-inputBox">
        <Input
          type="text"
          required
          label={this.withRequired(this.props.sendNameLabel)}
          name="name"
          value={this.state.name}
          onChange={this.handleInputValue}
          isValid={inputValidator().validateFieldName(this.state.name)}
          autoComplete="given-name"
        />
        </InputBox>
        <InputBox id="sharedMailComponent-inputBox">
        <Input
          type="email"
          required
          label={this.withRequired(this.props.sendEmailLabel)}
          name="email"
          value={this.state.email}
          onChange={this.handleInputValue}
          isValid={inputValidator().validateFieldEmail(this.state.email)}
          autoComplete="email"
        />
        </InputBox>
        {this.state.displayKey === display_send_to_others && (
          <>
            <Input
              type="text"
              required
              label={this.withRequired(this.props.shareRecipientNameLabel)}
              name="recipientName"
              value={this.state.recipientName}
              onChange={this.handleInputValue}
              isValid={inputValidator().validateFieldName(
                this.state.recipientName
              )}
              autoComplete="off"
            />
            <Input
              type="email"
              required
              label={this.withRequired(this.props.shareRecipientEmailLabel)}
              name="recipientEmail"
              value={this.state.recipientEmail}
              onChange={this.handleInputValue}
              isValid={inputValidator().validateFieldEmail(
                this.state.recipientEmail
              )}
              autoComplete="off"
            />
            <Label>{this.props.shareMessageLabel}</Label>
            <Message
              rows={6}
              name="message"
              onChange={this.handleTextareaValue}
              value={this.state.message}
            />
          </>
        )}

        {this.props.showSubscribe && <SubscribeWrapper visible={true}>
          <SubscribeCheckbox>
            <Checkbox
              value={this.state.subscribe}
              onChange={() =>
                this.handleCheckboxInputValue(!this.state.subscribe)
              }
              primaryColor={this.props.primaryColor}
            />
          </SubscribeCheckbox>
          <SubscribeText primaryColor={this.props.primaryColor}>
            <span
              onClick={() =>
                this.handleCheckboxInputValue(!this.state.subscribe)
              }
            >
              {this.props.policyLabel + " "}
            </span>
            <a
              href={this.props.policyLink}
              target="_blank"
              rel="noreferrer"
            >
              {this.props.privacyPolicyLinkLabel}
            </a>
          </SubscribeText>
        </SubscribeWrapper>}

        <Captcha>
          <RecaptchaContainer
            recaptchaKey={this.props.recaptchaKey}
            onChange={(value) =>
              this.handleInputValue({ target: { name: "recaptcha", value } })
            }
          />
        </Captcha>
        <ButtonsGroup>
          <qds-button
            size="large"
            text={this.props.cancelLabel}
            onClick={() => this.props.onCancel()}
          ></qds-button>
          <qds-button
            importance="emphasized"
            size="large"
            text={this.props.submitLabel}
            onClick={this.submitForm}
            {...disabledProps}
          ></qds-button>
        </ButtonsGroup>
      </TabContent>
    );
  };

  render = () => {
    return (
      <Content>
        {this.renderTabOptions()}
        {this.renderTabContent()}
      </Content>
    );
  };
}

export default SharedMailComponent;
